import { useState, useRef, useMemo, useEffect } from 'react'
import { observer } from 'mobx-react'
import { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { useMutation, useStore } from '../../../../../utils/mst-hooks'
import ApplicantCard from '../applicant-card/applicant-card'
import applicantsIcon from '../../assets/applicants.svg'
import dropDownIcon from '../../assets/down-arrow.svg'
import Spacer from '../../../../order/components/spacer/spacer'
import LoadingSpinner from '../../../../../components/loading-spinner/loading-spinner'
import { ChiefPopup } from '../chief-popup/chief-popup'
import './applicants-section.scss'
import { withErrorBoundary } from '../../../../../components/error/with-error-boundary'
import { Interview } from '../../../components/interview/interview'
import {
  CherryPickingApplicantSnapshot,
  CherryPickingApplicantOfPermanentJobSnapshot,
  CherryPickingApplicantOfPermanentJob,
  CherryPickingApplicant,
  ApplicantStatus,
} from '../../../../../models/cherry-picking-applicants'
import { Interview as InterviewInstance } from '../../../../../models/interview'
import { trackingService, TrackedEvent } from '../../../../../services/tracking-service'
import { useBottomListListener } from '../../../../../utils/use-bottom-list-listener'
import JobDetailsCard from '../job-details-card/job-details-card'
import { CherryPickingJob } from '../../../../../models/cherry-picking-jobs'
import { useAcceptApplicant } from '../../../../../utils/use-accept-applicant'
import { JobScheduleType } from '../../../../../models/job'
import { Popup } from '../../../../../components/popup/popup'
import ApplicantDetails from '../../../applicant-details/applicant-details'

enum sortByCategories {
  rating = 'rating',
  experience = 'years_experience',
  age = 'age',
}

type ApplicantsSectionInterface = {
  jobId: string
  jobType: JobScheduleType
  shiftId?: number
  cherryPickingJob: CherryPickingJob | null
}

const ApplicantsSection = withErrorBoundary(
  observer(({ jobId, jobType, shiftId, cherryPickingJob }: ApplicantsSectionInterface) => {
    const {
      getCurrentApplicant,
      applicantsView,
      setCurrentApplicant,
      clearCurrentApplicant,
      getCherryPickingApplicantsApiCall,
      getInterviewByJobId,
      getInterviewQuestions,
      getSpAnswer,
    } = useStore().data
    const isPermanentJob = jobType === JobScheduleType.permanent
    const applicants = applicantsView(jobType)
    const currentApplicant = getCurrentApplicant(jobType)
    const [sortBy, setSortBy] = useState('rating')
    const [isSortingLoading, setIsSortingLoading] = useState(false)
    const [isPaginationLoading, setIsPaginationLoading] = useState(false)
    const [showChiefModal, setShowChiefModal] = useState(false)
    const [currentQuestion, setCurrentQuestion] = useState<{ qId: number; qIndex: number } | null>(null)
    const [prioritizedApplicantIds, setPrioritizedApplicantIds] = useState<number[]>([])

    const [getCherryPickingApplicantsQuery, getCherryPickingApplicants] = useMutation(
      getCherryPickingApplicantsApiCall,
      {
        onSuccess: () => {
          setIsSortingLoading(false)
          setIsPaginationLoading(false)
        },
      },
    )
    const { acceptApplicant } = useAcceptApplicant({
      isPermanentJob,
      onAccepted: () => {
        toast.success(t('job_cherry_picking.applicant_assigned'))
      },
      onAcceptFailed: error => {
        toast.error(error.data.message)
      },
    })

    const showedChief = useRef(false)
    const chiefOg = useMemo(() => {
      if (isPermanentJob || applicants.list.length === 0) {
        return null
      }
      return applicants.list.find(applicant => (applicant as CherryPickingApplicantSnapshot).is_chief)
    }, [applicants])
    useEffect(() => {
      if (chiefOg && !showedChief.current) {
        setShowChiefModal(true)
        showedChief.current = true
        trackingService.track(TrackedEvent.ChiefCongratulationsPopupShown)
      }
    }, [chiefOg, showedChief.current])

    const onSortApplicants = ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
      setIsSortingLoading(true)
      setSortBy(value)
      getCherryPickingApplicants({
        jobId: Number(jobId),
        jobType,
        sortBy: value,
        shiftId,
      })
    }
    const onNextPage = () => {
      // Guard against duplicate requests while loading or when no next page
      if (isPaginationLoading || getCherryPickingApplicantsQuery.isLoading) return
      const next = applicants.paginator?.next_page
      if (!next) return

      setIsPaginationLoading(true)
      getCherryPickingApplicants({
        jobId: Number(jobId),
        jobType,
        sortBy,
        page: next,
        shiftId,
      })
    }

    const handleScroll = useBottomListListener(() => {
      onNextPage()
    })

    const { t } = useTranslation()

    return (
      <div className="ApplicantsSection" onScroll={handleScroll}>
        <JobDetailsCard
          jobDetails={cherryPickingJob}
          showQuantity={!isPermanentJob}
          showFulfillment={!isPermanentJob}
          showStartDate={!isPermanentJob}
          showEndDate={!isPermanentJob}
        />
        <Spacer space={22} />
        <div className="ApplicantsSection__headerContainer">
          <div className="ApplicantsSection__titleContainer">
            <img src={applicantsIcon} />
            <h3 className="ApplicantsSection__title">
              {t('job_cherry_picking.total_applicants', { count: applicants.paginator?.total })}
            </h3>
          </div>
          {!isPermanentJob && (
            <div className="ApplicantsSection__sorting">
              <h4 className="ApplicantsSection__sortBy">{t('job_cherry_picking.sort_by')} </h4>
              <select value={sortBy} onChange={onSortApplicants} className="ApplicantsSection__sortingDropDown">
                <option value={sortByCategories.rating}>{t('job_cherry_picking.rating')}</option>
                <option value={sortByCategories.experience}>{t('job_cherry_picking.experience')}</option>
                <option value={sortByCategories.age}>{t('job_cherry_picking.age')}</option>
              </select>
              <img src={dropDownIcon} />
            </div>
          )}
        </div>
        <Spacer space={24} />
        {getCherryPickingApplicantsQuery.isLoading && isSortingLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="ApplicantsSection__list">
              {isPermanentJob
                ? applicants.list.length > 4 &&
                  (applicants.list as CherryPickingApplicant[])
                    .filter(applicant => prioritizedApplicantIds.includes(applicant.id))
                    .map((applicant, i: number) => (
                      <div key={i}>
                        <ApplicantCard
                          jobId={jobId}
                          jobType={jobType}
                          applicant={{
                            id: applicant.id,
                            name: applicant.name,
                            age: applicant.age,
                            image_url: applicant.image_url,
                            languages: applicant.languages,
                            nationality: applicant.nationality,
                            is_favorite: Boolean(applicant.is_favorite),
                            skillMatch: applicant.skillMatch,
                            is_chief: applicant.is_chief,
                            rating: applicant.rating,
                            top_qualities: applicant.top_qualities,
                            years_experience: applicant.years_experience,
                            skills: applicant.skills,
                            status: applicant.isAccepted ? ApplicantStatus.SHORTLISTED : ApplicantStatus.PENDING,
                          }}
                          shiftId={shiftId}
                          onPlayAnswersPress={() => {
                            setCurrentApplicant(applicant.id, jobType)
                            const interview = getInterviewByJobId(parseInt(jobId)) as InterviewInstance
                            const questionIds = getInterviewQuestions(interview.id)
                            const applicantQuestions = questionIds.filter(q => {
                              const answer = getSpAnswer({
                                spId: applicant.id,
                                interviewId: interview.id,
                                questionId: q,
                              })
                              return Boolean(answer)
                            })
                            setCurrentQuestion({ qId: applicantQuestions[0], qIndex: 0 })
                          }}
                          onAccepted={(applicantId: number) => {
                            if (!prioritizedApplicantIds.includes(applicantId)) {
                              setPrioritizedApplicantIds([...prioritizedApplicantIds, applicantId])
                            }
                          }}
                        />
                        <Spacer space={i === applicants.list.length - 1 ? 28 : 14} />
                      </div>
                    ))
                : null}
              {applicants.list.map(
                (item: CherryPickingApplicantSnapshot | CherryPickingApplicantOfPermanentJobSnapshot, i: number) => {
                  const applicantOfPermanentJob = item as CherryPickingApplicantOfPermanentJob
                  const applicant = item as CherryPickingApplicant
                  return (
                    <div key={i}>
                      <ApplicantCard
                        jobId={jobId}
                        jobType={jobType}
                        applicant={
                          isPermanentJob
                            ? {
                                id: applicantOfPermanentJob.job_application_id,
                                name: applicantOfPermanentJob.full_name,
                                age: applicantOfPermanentJob.age,
                                image_url: applicantOfPermanentJob.profile_photo,
                                languages: applicantOfPermanentJob.spoken_languages,
                                nationality: applicantOfPermanentJob.nationality,
                                status: applicantOfPermanentJob.client_status_id,
                              }
                            : {
                                id: applicant.id,
                                name: applicant.name,
                                age: applicant.age,
                                image_url: applicant.image_url,
                                languages: applicant.languages,
                                nationality: applicant.nationality,
                                is_favorite: Boolean(applicant.is_favorite),
                                skillMatch: applicant.skillMatch,
                                is_chief: applicant.is_chief,
                                rating: applicant.rating,
                                top_qualities: applicant.top_qualities,
                                years_experience: applicant.years_experience,
                                skills: applicant.skills,
                                status: applicant.isAccepted ? ApplicantStatus.SHORTLISTED : ApplicantStatus.PENDING,
                              }
                        }
                        shiftId={shiftId}
                        onPlayAnswersPress={() => {
                          setCurrentApplicant(applicant.id, jobType)
                          const interview = getInterviewByJobId(parseInt(jobId)) as InterviewInstance
                          const questionIds = getInterviewQuestions(interview.id)
                          const applicantQuestions = questionIds.filter(q => {
                            const answer = getSpAnswer({ spId: applicant.id, interviewId: interview.id, questionId: q })
                            return Boolean(answer)
                          })
                          setCurrentQuestion({ qId: applicantQuestions[0], qIndex: 0 })
                        }}
                        onAccepted={(applicantId: number) => {
                          if (!prioritizedApplicantIds.includes(applicantId)) {
                            setPrioritizedApplicantIds([...prioritizedApplicantIds, applicantId])
                          }
                        }}
                      />
                      <Spacer space={i === applicants.list.length - 1 ? 28 : 14} />
                    </div>
                  )
                },
              )}
              {applicants.paginator?.next_page ? (
                getCherryPickingApplicantsQuery.isLoading && isPaginationLoading ? (
                  <LoadingSpinner />
                ) : null
              ) : null}
            </div>
          </>
        )}
        {showChiefModal && chiefOg && (
          <ChiefPopup
            name={chiefOg.name ?? 'Applicant'}
            imgSrc={chiefOg.image ?? ''}
            onClose={() => {
              setShowChiefModal(false)
            }}
          />
        )}
        {!isPermanentJob && (
          <Interview
            applicantsType="multiple"
            jobId={parseInt(jobId)}
            showInitialQuestion={currentQuestion}
            onAcceptPress={() => {
              acceptApplicant({
                sp_id: String(currentApplicant?.id),
                job_id: parseInt(jobId),
                shift_ids: shiftId ? [shiftId] : undefined,
              })
            }}
          />
        )}
        <Popup
          isActive={currentApplicant !== null}
          content={<ApplicantDetails jobId={String(jobId)} jobType={jobType} showHeader={false} showControls />}
          close={() => clearCurrentApplicant(jobType)}
        />
      </div>
    )
  }),
)

export default ApplicantsSection
